/**
 * Impersonation Token Models
 *
 * TypeScript interfaces for JWT token claims used in impersonation. Based on the actual token structure from
 * AuthCentral.
 *
 * @packageDocumentation
 */

/**
 * Standard JWT claims present in all authentication tokens
 *
 * These claims are issued by AuthCentral and present in both normal authentication tokens and impersonation tokens.
 */
export interface JWTClaims {
  /** Issuer (e.g., "https://authcentral.platformapidev.complexcaresolutions.com/") */
  readonly iss: string;

  /** Expiration time (Unix timestamp) */
  readonly exp: number;

  /** Issued at (Unix timestamp) */
  readonly iat: number;

  /** JWT ID (unique token identifier) */
  readonly jti: string;

  /** Subject (user email) */
  readonly sub: string;

  /** User name */
  readonly userName: string;

  /** User ID */
  readonly userId: string;

  /** User email */
  readonly userEmail: string;

  /** Primary client ID */
  readonly primayClientId: string; // Note: Typo in actual token

  /** Array of entitlement codes */
  readonly entitlement: string[];

  /** Role ID */
  readonly roleId: string;

  /** Client ID */
  readonly clientId: string;

  /** Should roll back transaction flag */
  readonly shouldRollBackTransaction: string;

  /** OpenID Connect preset */
  readonly oi_prst: string;

  /** OpenID Connect client ID */
  readonly client_id: string;

  /** OpenID Connect token ID (used for encryption key derivation) */
  readonly oi_tkn_id: string;
}

/**
 * Extended JWT claims for impersonation tokens
 *
 * When admin impersonates a user, the backend issues a new JWT with additional claims to identify the impersonation
 * context.
 */
export interface ImpersonationJWTClaims extends JWTClaims {
  /** Flag indicating this is an impersonation token Only present and true during impersonation */
  readonly isImpersonating: true;

  /** Original admin's user ID (who initiated impersonation) Only present during impersonation */
  readonly originalUserId: string;

  /**
   * Unique session identifier for this impersonation session Only present during impersonation Used for backend
   * validation and encryption key derivation
   */
  readonly impersonationSessionId: string;
}

/**
 * Type guard to check if JWT claims represent an impersonation token
 *
 * @example
 *   ```typescript
 *   const claims = parseJWT(token);
 *   if (isImpersonating(claims)) {
 *     console.log('Impersonating user:', claims.userId);
 *     console.log('Original admin:', claims.originalUserId);
 *   }
 *   ```;
 *
 * @param claims - JWT claims to check
 * @returns True if claims represent an active impersonation session
 */
export function isImpersonating(claims: JWTClaims): claims is ImpersonationJWTClaims {
  return 'isImpersonating' in claims && claims.isImpersonating === true;
}

/**
 * Encrypted token structure stored in localStorage
 *
 * Original admin token is encrypted using AES-GCM before storage to prevent token theft or tampering.
 */
export interface EncryptedToken {
  /** Base64-encoded ciphertext (encrypted JWT) */
  readonly ciphertext: string;

  /** Base64-encoded initialization vector (12 bytes for AES-GCM) */
  readonly iv: string;

  /** Base64-encoded authentication tag (16 bytes for AES-GCM) */
  readonly tag: string;

  /** Base64-encoded salt used for PBKDF2 key derivation (16 bytes) */
  readonly salt: string;

  /** Encryption version (for future format changes) */
  readonly version: number;

  /** ISO 8601 timestamp when token was encrypted */
  readonly encryptedAt: string;
}

/** Encryption configuration constants */
export const ENCRYPTION_CONFIG = {
  /** AES-GCM algorithm name */
  ALGORITHM: 'AES-GCM',

  /** Key length in bits (256-bit AES) */
  KEY_LENGTH: 256,

  /** IV length in bytes (12 bytes = 96 bits, recommended for AES-GCM) */
  IV_LENGTH: 12,

  /** Salt length in bytes for PBKDF2 */
  SALT_LENGTH: 16,

  /** Authentication tag length in bytes (16 bytes = 128 bits) */
  TAG_LENGTH: 16,

  /** PBKDF2 iteration count (OWASP recommendation for 2024+) */
  PBKDF2_ITERATIONS: 100_000,

  /** PBKDF2 hash algorithm */
  PBKDF2_HASH: 'SHA-256',

  /** Current encryption format version */
  VERSION: 1,
} as const;
